import { Button } from '@mui/material';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { Select } from '@/components/Form/Select';

import { getAssetPath } from '@/constants/configs';
import { NATIONALITIES_WITH_POPULAR } from '@/constants/countries';

import { useAppSelector } from '@/hooks/redux';
import { useQuestionInfoMutation } from '@/hooks/useQuestionInfoMutation';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { getDataTestId } from '@/utils/browser';

import { ActionBar } from '../../../../Layout';
import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

import { DualCitizenshipImage } from './QuestionDrawer.styled';

export function QuestionAboutDualCitizenship({
  onAnswered,
  question,
  ...props
}: Readonly<QuestionProps>) {
  const user = useAppSelector(selectLoggedInUser);
  const { nationality, t, country } = useTranslate();

  const questionKey = question.extraProps?.questionKey as string | undefined;
  const displayQuestion = questionKey
    ? t(questionKey, {
        userCountry: country[user?.citizenship as string] || '',
      })
    : question.question;
  const {
    value: isYesAnswer,
    setTrue: onYes,
    setFalse: resetAnswer,
  } = useBoolean(false);
  const {
    handleSubmit,
    control,
    formState: { errors: visibleErrors, isValid },
    reset: resetForm,
  } = useForm<{
    citizenship2: string;
  }>({
    defaultValues: {
      citizenship2: '',
    },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useQuestionInfoMutation({
    onFinished: onAnswered,
    target: props.pepAnswerTarget,
  });

  const onAnswerNo = () => {
    mutate({
      citizenship2Has: false,
    });
  };

  const onCancel = () => {
    resetAnswer();
    resetForm();
  };

  const onSubmit = handleSubmit(async payload => {
    mutate({
      citizenship2Has: true,
      citizenship2: payload.citizenship2,
    });
  });

  const nationalityOptions = NATIONALITIES_WITH_POPULAR.filter(
    item => item.value !== user?.citizenship,
  ).map(item => ({
    ...item,
    text: nationality[item.value],
  }));

  const renderDualCitizenshipImage = useMemo(() => {
    if (!user?.tenant) {
      return <DualCitizenshipImage />;
    }
    return (
      <DualCitizenshipImage
        src={getAssetPath(
          `/dual-citizenship/question-dual-citizenship-${user.tenant}.svg`,
        )}
      />
    );
  }, [user]);

  return (
    <QuestionForm
      {...props}
      question={{ ...question, question: displayQuestion }}
      image={renderDualCitizenshipImage}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      {!isYesAnswer ? (
        <ActionBar>
          <Button
            {...getDataTestId(`basic-info-yes-button`)}
            variant="outlined"
            fullWidth
            onClick={onYes}
          >
            {t('questionDrawer.dualCitizenship.button.yes')}
          </Button>
          <Button
            {...getDataTestId(`basic-info-no-button`)}
            variant="outlined"
            fullWidth
            onClick={onAnswerNo}
          >
            {t('questionDrawer.dualCitizenship.button.no')}
          </Button>
        </ActionBar>
      ) : (
        <>
          <Select
            options={nationalityOptions}
            control={control}
            name="citizenship2"
            error={!!visibleErrors.citizenship2}
            helperText={visibleErrors.citizenship2?.message}
            rules={{
              required: t(
                'questionDrawer.dualCitizenship.citizenship2.required',
              ),
            }}
            fullWidth
            placeholder={t(
              'questionDrawer.dualCitizenship.citizenship2.placeHolder',
            )}
            useDrawerOnMobile
            label={t('questionDrawer.dualCitizenship.citizenship2.label')}
            withSearchBar
            disabled={isPending}
          />
          <ActionBar>
            <Button
              {...getDataTestId(`basic-info-cancel-button`)}
              variant="outlined"
              fullWidth
              onClick={onCancel}
              disabled={isPending}
            >
              {t('questionDrawer.dualCitizenship.button.goBack')}
            </Button>
            <Button
              {...getDataTestId(`basic-info-confirm-button`)}
              variant="outlined"
              fullWidth
              type="submit"
              disabled={!isValid || isPending}
            >
              {t('questionDrawer.dualCitizenship.button.proceed')}
            </Button>
          </ActionBar>
        </>
      )}
      <FormErrorLogger
        formName="QuestionAboutDualCitizenship"
        control={control}
      />
    </QuestionForm>
  );
}
